import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { List, X, Phone } from "@phosphor-icons/react";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#services", label: "Services" },
    { href: "#tracking", label: "Track Repair" },
    { href: "#testimonials", label: "Testimonials" },
    { href: "#faq", label: "FAQ" },
    { href: "#contact", label: "Contact" },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass border-b border-slate-200" : "bg-transparent"
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="/"
            className="flex items-center gap-3"
            data-testid="logo-link"
          >
            <div className="w-10 h-10 bg-[#0B2F6B] flex items-center justify-center">
              <span className="text-white font-black text-xl font-['Outfit']">L</span>
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-bold text-[#1A1A2E] tracking-tight font-['Outfit'] leading-tight">
                LASER DIVISION
              </span>
              <span className="text-[10px] text-slate-500 tracking-[0.2em] uppercase">
                Cosmetic Laser Repair
              </span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className="nav-link text-sm font-medium text-[#7A7D82] hover:text-[#1A1A2E] transition-colors"
                data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:+18016479206"
              className="flex items-center gap-2 text-sm font-semibold text-[#1A1A2E]"
              data-testid="phone-link"
            >
              <Phone weight="fill" className="w-4 h-4 text-[#0B2F6B]" />
              (801) 647-9206
            </a>
            <Button
              onClick={() => scrollToSection("#contact")}
              className="bg-[#1E88FF] hover:bg-[#1976D2] text-white rounded-none px-6 font-semibold"
              data-testid="header-cta-button"
            >
              Get Quote
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-[#1A1A2E]" />
            ) : (
              <List className="w-6 h-6 text-[#1A1A2E]" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/30 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
          data-testid="mobile-menu-overlay"
        />
      )}

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed inset-y-0 right-0 w-80 bg-white shadow-2xl z-50 mobile-menu ${
          isMobileMenuOpen ? "open" : ""
        }`}
        data-testid="mobile-menu"
      >
        {/* Close Button Inside Menu */}
        <button
          className="absolute top-6 right-6 p-2 z-50"
          onClick={() => setIsMobileMenuOpen(false)}
          data-testid="mobile-menu-close"
        >
          <X className="w-6 h-6 text-slate-900" />
        </button>
        <div className="p-6 pt-24">
          <nav className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className="text-left text-lg font-medium text-[#1A1A2E] hover:text-[#0B2F6B] transition-colors py-2 border-b border-slate-100"
              >
                {link.label}
              </button>
            ))}
          </nav>
          <div className="mt-8 space-y-4">
            <a
              href="tel:+18016479206"
              className="flex items-center gap-2 text-lg font-semibold text-[#1A1A2E]"
            >
              <Phone weight="fill" className="w-5 h-5 text-[#0B2F6B]" />
              (801) 647-9206
            </a>
            <Button
              onClick={() => scrollToSection("#contact")}
              className="w-full bg-[#1E88FF] hover:bg-[#1976D2] text-white rounded-none py-6 font-semibold"
            >
              Get Quote
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
